import { DateTime } from "luxon";
import { useState } from "react";
import { useQuery } from "react-query";

export type EmailActivityPage = {
  total: number;
  emails: Array<{
    id: number;
    verification_type: string;
    recipient: string;
    subject: string;
    reason?: string | null;
    status: string;
    sent_at?: string | null;
    createdAt: string;
  }>;
};

const labels: Record<string, string> = {
  sent: "Sent — accepted by mail server",
  failed: "Failed to send",
  skipped: "Not sent — development environment",
  sending: "Sending / outcome not yet confirmed",
};

export default function VerificationEmailActivity({
  reviewId,
  fetchEmails,
}: {
  reviewId: number;
  fetchEmails: (id: number, page: number) => Promise<EmailActivityPage>;
}) {
  const [page, setPage] = useState(1);
  const query = useQuery(["verificationEmails", reviewId, page], () =>
    fetchEmails(reviewId, page),
  );
  return (
    <section aria-label="Email activity" className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold">Verification emails</h3>
        <button
          onClick={() => query.refetch()}
          className="px-3 py-2 text-sm font-semibold border rounded-lg"
        >
          Refresh emails
        </button>
      </div>
      <p className="text-sm text-gray-500">
        Recorded send attempts since email tracking was enabled. Earlier emails
        may not appear. Sent means the mail server accepted the message; inbox
        delivery is not confirmed.
      </p>
      {query.isLoading && <p role="status">Loading email activity…</p>}
      {query.isError && (
        <p role="alert" className="text-rose-700">
          Unable to load email activity. Please refresh emails.
        </p>
      )}
      {!query.isLoading && !query.isError && !query.data?.emails.length && (
        <p>No recorded verification emails for this user.</p>
      )}
      {query.data?.emails.map((email) => (
        <article
          key={email.id}
          className="p-4 space-y-2 border border-gray-200 rounded-xl"
        >
          <h4 className="font-semibold">{email.subject}</h4>
          <p className="text-sm">
            {email.status === "skipped" && !email.recipient.trim()
              ? "Not sent — no email address"
              : labels[email.status] || "Unknown send status"}
          </p>
          <p className="text-sm text-gray-600 break-all">
            To: {email.recipient.trim() || "No email address"}
          </p>
          <p className="text-xs text-gray-500 capitalize">
            {email.verification_type} ·{" "}
            {DateTime.fromISO(email.sent_at || email.createdAt).toFormat(
              "DD t",
            )}
          </p>
          {email.reason && (
            <p className="text-sm whitespace-pre-wrap">
              Reason: {email.reason}
            </p>
          )}
        </article>
      ))}
      {(query.data?.total ?? 0) > 20 && (
        <nav aria-label="Email pagination" className="flex items-center gap-4">
          <button
            disabled={page === 1 || query.isFetching}
            onClick={() => setPage(page - 1)}
          >
            Previous emails
          </button>
          <span>Page {page}</span>
          <button
            disabled={page * 20 >= (query.data?.total ?? 0) || query.isFetching}
            onClick={() => setPage(page + 1)}
          >
            Next emails
          </button>
        </nav>
      )}
    </section>
  );
}
